import { useState, type FormEvent } from "react";
import { generateClient } from "aws-amplify/data";
import type { Schema } from "amplify/data/resource";
import { Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { presentTrackNames, stagesForTrack } from "./lifecycle-sheet-helpers";
import type { TrackState } from "../types";

const client = generateClient<Schema>();

interface LifecycleTransitionFormProps {
  businessId: string;
  tracks: Record<string, TrackState>;
  onTransitionComplete: () => void | Promise<void>;
}

export function LifecycleTransitionForm({
  businessId,
  tracks,
  onTransitionComplete,
}: LifecycleTransitionFormProps) {
  const trackNames = presentTrackNames(tracks);
  const [open, setOpen] = useState(false);
  const [track, setTrack] = useState(trackNames[0] ?? "");
  const [to, setTo] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  if (trackNames.length === 0) return null;

  const targets = stagesForTrack(track);

  function reset() {
    setOpen(false);
    setTo("");
    setReason("");
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!businessId || !track || !to || !reason.trim()) return;
    setLoading(true);
    try {
      const { errors } = await client.mutations.AdminTransitionLifecycleStage({
        input: { businessId, track, to, reason: reason.trim() },
      });
      if (errors?.length) throw new Error(errors[0].message);
      toast.success("Stage transitioned successfully");
      reset();
      await onTransitionComplete();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Transition failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mb-6">
      {!open ? (
        <button
          className="border-border bg-card text-foreground hover:bg-muted rounded-md border px-3 py-1.5 text-sm font-medium"
          onClick={() => {
            setTrack(trackNames[0] ?? "");
            setTo("");
            setOpen(true);
          }}
        >
          Transition stage
        </button>
      ) : (
        <div className="border-border bg-muted rounded-md border p-4">
          <h3 className="text-foreground mb-3 text-sm font-semibold">
            Manual transition
          </h3>
          <form
            onSubmit={(e) => {
              void handleSubmit(e);
            }}
          >
            <div className="mb-3 flex flex-col gap-1">
              <label
                htmlFor="lifecycle-transition-track"
                className="text-muted-foreground text-xs font-medium"
              >
                Track
              </label>
              <select
                id="lifecycle-transition-track"
                value={track}
                onChange={(e) => {
                  setTrack(e.target.value);
                  setTo("");
                }}
                className="border-border bg-card text-foreground rounded-md border px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
              >
                {trackNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-3 flex flex-col gap-1">
              <label
                htmlFor="lifecycle-transition-to"
                className="text-muted-foreground text-xs font-medium"
              >
                To
              </label>
              <select
                id="lifecycle-transition-to"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="border-border bg-card text-foreground rounded-md border px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
              >
                <option value="">Select stage…</option>
                {targets.map((stage) => (
                  <option key={stage} value={stage}>
                    {stage}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-3 flex flex-col gap-1">
              <label
                htmlFor="lifecycle-transition-reason"
                className="text-muted-foreground text-xs font-medium"
              >
                Reason
              </label>
              <input
                id="lifecycle-transition-reason"
                type="text"
                required
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter reason for transition"
                className="border-border bg-card text-foreground placeholder:text-subtle-foreground rounded-md border px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>
            <p className="text-muted-foreground mb-4 text-xs">
              Derived tracks (onboarding, payouts) may be re-derived on the next
              Stripe signal — a manual override can be transient.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={reset}
                className="border-border bg-card text-foreground hover:bg-muted rounded-md border px-3 py-1.5 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !to || !reason.trim()}
                className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Transition
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
